import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";

import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Loader2, Package, Search, X } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/settings/suppliers")({
	component: SupplierDraftsPage,
});

const client = createClient(CatalogService, transport);

const STATUS_LABELS: Record<string, string> = {
	pending: "На модерации",
	approved: "Одобрено",
	rejected: "Отклонено",
	revision_requested: "Доработка",
};

const STATUS_COLORS: Record<string, string> = {
	pending: "bg-yellow-100 text-yellow-800",
	approved: "bg-green-100 text-green-800",
	rejected: "bg-red-100 text-red-800",
	revision_requested: "bg-blue-100 text-blue-800",
};

type PendingAction =
	| { type: "approve"; draftId: string }
	| { type: "reject"; draftId: string };

function SupplierDraftsPage() {
	const qc = useQueryClient();
	const [statusFilter, setStatusFilter] = useState("pending");
	const [search, setSearch] = useState("");
	const [pendingAction, setPendingAction] = useState<PendingAction | null>(
		null,
	);
	const [rejectNotes, setRejectNotes] = useState("");

	const { data, isLoading, error } = useQuery({
		queryKey: ["supplier-drafts", statusFilter],
		queryFn: () =>
			client.listSupplierDrafts({
				status: statusFilter === "_all" ? "" : statusFilter,
				page: { pageSize: 50 },
			}),
	});
	const drafts = data?.drafts ?? [];

	const reviewMut = useMutation({
		mutationFn: ({
			draftId,
			action,
			notes,
		}: { draftId: string; action: string; notes?: string }) =>
			client.reviewSupplierDraft({ draftId, action, notes }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["supplier-drafts"] }),
	});

	function handleConfirm() {
		if (!pendingAction) return;
		if (pendingAction.type === "approve") {
			reviewMut.mutate({ draftId: pendingAction.draftId, action: "approve" });
		} else {
			reviewMut.mutate({
				draftId: pendingAction.draftId,
				action: "reject",
				notes: rejectNotes,
			});
		}
		setPendingAction(null);
		setRejectNotes("");
	}

	function handleCancel() {
		setPendingAction(null);
		setRejectNotes("");
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder="Поиск поставщиков…"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9 w-56"
					/>
				</div>
				<Select value={statusFilter} onValueChange={setStatusFilter}>
					<SelectTrigger className="w-48">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="_all">Все</SelectItem>
						<SelectItem value="pending">На модерации</SelectItem>
						<SelectItem value="approved">Одобренные</SelectItem>
						<SelectItem value="rejected">Отклонённые</SelectItem>
						<SelectItem value="revision_requested">На доработке</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{isLoading && (
				<div className="flex justify-center py-12">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			)}

			{error && (
				<Card className="flex flex-col items-center py-12 gap-3">
					<p className="text-sm text-destructive">
						Ошибка загрузки: {String(error)}
					</p>
				</Card>
			)}

			{!isLoading && !error && drafts.length === 0 && (
				<Card className="flex flex-col items-center py-12 gap-3">
					<Package className="h-10 w-10 text-muted-foreground" />
					<p className="text-sm text-muted-foreground">
						Нет черновиков от поставщиков
					</p>
				</Card>
			)}

			{drafts.length > 0 && (
				<TooltipProvider>
					<div className="rounded-lg border overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="bg-muted/30">
									<TableHead>Название</TableHead>
									<TableHead>Поставщик</TableHead>
									<TableHead>Статус</TableHead>
									<TableHead className="w-24">Действия</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{drafts.map((d) => (
									<TableRow key={d.id}>
										<TableCell className="font-medium">
											<Tooltip>
												<TooltipTrigger asChild>
													<span className="cursor-default line-clamp-1">
														{d.title}
													</span>
												</TooltipTrigger>
												<TooltipContent>{d.title}</TooltipContent>
											</Tooltip>
										</TableCell>
										<TableCell className="text-muted-foreground text-sm">
											<Tooltip>
												<TooltipTrigger asChild>
													<span className="cursor-default font-mono">
														{d.supplierPartyId.slice(0, 8)}...
													</span>
												</TooltipTrigger>
												<TooltipContent className="font-mono text-xs">
													{d.supplierPartyId}
												</TooltipContent>
											</Tooltip>
										</TableCell>
										<TableCell>
											<Badge
												className={`text-[10px] ${STATUS_COLORS[d.status] ?? ""}`}
												variant="secondary"
											>
												{STATUS_LABELS[d.status] ?? d.status}
											</Badge>
										</TableCell>
										<TableCell>
											{d.status === "pending" && (
												<div className="flex gap-1">
													<Button
														size="sm"
														variant="ghost"
														className="h-7 w-7 p-0 text-green-600"
														onClick={() =>
															setPendingAction({
																type: "approve",
																draftId: d.id,
															})
														}
														disabled={reviewMut.isPending}
													>
														<Check className="h-4 w-4" />
													</Button>
													<Button
														size="sm"
														variant="ghost"
														className="h-7 w-7 p-0 text-destructive"
														onClick={() =>
															setPendingAction({
																type: "reject",
																draftId: d.id,
															})
														}
														disabled={reviewMut.isPending}
													>
														<X className="h-4 w-4" />
													</Button>
												</div>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</TooltipProvider>
			)}

			{/* Approve confirmation dialog */}
			<AlertDialog
				open={pendingAction?.type === "approve"}
				onOpenChange={(open) => {
					if (!open) handleCancel();
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Одобрить товар?</AlertDialogTitle>
						<AlertDialogDescription>
							Он будет опубликован в каталог.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Отмена</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirm}>
							Одобрить
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Reject confirmation dialog */}
			<AlertDialog
				open={pendingAction?.type === "reject"}
				onOpenChange={(open) => {
					if (!open) handleCancel();
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Отклонить товар?</AlertDialogTitle>
					</AlertDialogHeader>
					<Textarea
						className="mt-2"
						placeholder="Причина отклонения (необязательно)"
						value={rejectNotes}
						onChange={(e) => setRejectNotes(e.target.value)}
						rows={3}
					/>
					<AlertDialogFooter className="mt-4">
						<AlertDialogCancel>Отмена</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							onClick={handleConfirm}
						>
							Отклонить
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
